import { useState, useEffect, useRef, useMemo } from "react";
import { COMPLETION_ANIMATION_MS } from "@/config/constants";

interface CompletionItem {
  id: string;
  status: string;
}

interface UseCompletionAnimationOptions<T extends CompletionItem> {
  /** Items currently in pending/processing state */
  rawPendingItems: T[];
  /** Function to look up an item by ID (to check its final status) */
  getItemById: (id: string) => T | undefined;
}

interface UseCompletionAnimationResult<T extends CompletionItem> {
  /** IDs currently showing the completion animation */
  completingIds: Set<string>;
  /** Pending items + completing items (so they still render as cards) */
  pendingItems: T[];
}

export function useCompletionAnimation<T extends CompletionItem>({
  rawPendingItems,
  getItemById,
}: UseCompletionAnimationOptions<T>): UseCompletionAnimationResult<T> {
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());
  const completingIdsRef = useRef(completingIds);
  completingIdsRef.current = completingIds;
  const prevPendingIdsRef = useRef<Set<string>>(new Set());
  const completingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const currentIds = new Set(rawPendingItems.map((item) => item.id));
    const prevIds = prevPendingIdsRef.current;

    // Detect items that left pending (were pending, no longer pending)
    for (const id of prevIds) {
      if (!currentIds.has(id) && !completingIdsRef.current.has(id)) {
        const item = getItemById(id);
        if (item && item.status === "completed") {
          setCompletingIds((prev) => new Set(prev).add(id));

          const timer = setTimeout(() => {
            setCompletingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            completingTimersRef.current.delete(id);
          }, COMPLETION_ANIMATION_MS);
          completingTimersRef.current.set(id, timer);
        }
      }
    }

    prevPendingIdsRef.current = currentIds;
  }, [rawPendingItems, getItemById]);

  // Clean up timers on unmount
  useEffect(() => {
    const timers = completingTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Include completing items so they still render as progress cards
  const pendingItems = useMemo(() => {
    if (completingIds.size === 0) return rawPendingItems;
    const completingItems = Array.from(completingIds)
      .map((id) => getItemById(id))
      .filter(Boolean) as T[];
    return [...rawPendingItems, ...completingItems];
  }, [rawPendingItems, completingIds, getItemById]);

  return { completingIds, pendingItems };
}
